/**
 * <grid-items-switch>
 *
 * Standalone control that lets the customer choose how many columns the items
 * of ANOTHER section should be rendered in (1 or 2) for mobile and (3 or 4) for desktop. It is fully decoupled from
 * the target grid: it only broadcasts the chosen value in three ways so any
 * section can consume it.
 *
 *   1. CSS variable  → sets a configurable CSS variable on <html> (document root).
 *                      Default: `--grid-items-per-row`. Override with the `variable` attribute.
 *                      A target grid can do:
 *                      grid-template-columns: repeat(var(--grid-items-per-row, 4), minmax(0, 1fr));
 *   2. localStorage  → persists the choice under the variable name (optional).
 *   3. DOM event     → dispatches `grid-items:change` on `document` with
 *                      detail `{ value: <Number>, variable: <String> }`.
 *
 * Markup (rendered by sections/number-grid-items.liquid):
 *   <!-- Desktop: 3 or 4 columns -->
 *   <grid-items-switch default-value="4" persist="true" variable="--grid-items-per-row">
 *     <button type="button" data-value="3">…3x3 icon…</button>
 *     <button type="button" data-value="4">…4x4 icon…</button>
 *   </grid-items-switch>
 *
 *   <!-- Mobile: 1 or 2 columns -->
 *   <grid-items-switch default-value="2" persist="true" variable="--grid-items-per-row-mobile">
 *     <button type="button" data-value="1">…1 col…</button>
 *     <button type="button" data-value="2">…2 col…</button>
 *   </grid-items-switch>
 */
class GridItemsSwitch extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('button[data-value]'));
    if (this.buttons.length === 0) return;

    this.persist = this.getAttribute('persist') !== 'false';
    this.defaultValue = this.getAttribute('default-value') || '4';
    this.variable = this.getAttribute('variable') || '--grid-items-per-row';

    var initial = this.defaultValue;
    if (this.persist) {
      var stored = this.readStored();
      if (stored) initial = stored;
    }

    this.buttons.forEach(
      function (button) {
        button.addEventListener(
          'click',
          function () {
            this.select(button.dataset.value);
          }.bind(this),
        );
      }.bind(this),
    );

    // Apply the initial state (sets CSS var + fires the event so the target
    // grid is in sync as soon as the page loads).
    this.select(initial);
  }

  select(value) {
    var isKnown = this.buttons.some(function (button) {
      return button.dataset.value === value;
    });
    if (!isKnown) value = this.defaultValue;

    this.value = value;

    this.buttons.forEach(function (button) {
      var isActive = button.dataset.value === value;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    document.documentElement.style.setProperty(this.variable, value);

    if (this.persist) this.writeStored(value);

    document.dispatchEvent(
      new CustomEvent('grid-items:change', {
        detail: { value: parseInt(value, 10), variable: this.variable },
      }),
    );
  }

  readStored() {
    try {
      return localStorage.getItem(this.variable);
    } catch (e) {
      return null;
    }
  }

  writeStored(value) {
    try {
      localStorage.setItem(this.variable, value);
    } catch (e) {
      /* storage unavailable (private mode) — ignore */
    }
  }
}

if (!customElements.get('grid-items-switch')) {
  customElements.define('grid-items-switch', GridItemsSwitch);
}
